import { TRACKING_FIELD } from 'constants/trackingEvents';
import { SitemapTrackingFields } from 'models';

const LOCALE_SHORT_RE = /[a-z]{2}/;

export const SITEMAP_FIELD_ORDER = [
	TRACKING_FIELD.PP_EN,
	TRACKING_FIELD.PP_ES,
	TRACKING_FIELD.PP_FR,
	TRACKING_FIELD.PAGE_ALIAS,
	TRACKING_FIELD.PAGE_ID,
	TRACKING_FIELD.HTML,
	TRACKING_FIELD.XML,
	TRACKING_FIELD.XML_ADD_DATE,
	TRACKING_FIELD.XML_REMOVE_DATE,
	TRACKING_FIELD.NO_INDEX,
	TRACKING_FIELD.NO_FOLLOW
] as const;

export type SitemapFieldName = (typeof SITEMAP_FIELD_ORDER)[number];

export const localeToTrackingField = (locale: string): string => {
	const match = locale.toLowerCase().match(LOCALE_SHORT_RE);
	const shortLocale = match?.[0] ?? 'unknown';
	return `PP_${shortLocale}`;
};

export const getSitemapChangedFields = (
	current: SitemapTrackingFields,
	initial: SitemapTrackingFields
): SitemapFieldName[] => {
	const changed = new Set<string>();

	current.localePaths?.forEach((localePath, idx) => {
		if (localePath.path !== initial.localePaths?.[idx]?.path) {
			changed.add(localeToTrackingField(localePath.locale) as typeof SITEMAP_FIELD_ORDER[number]);
		}
	});

	if (current.pageAlias !== initial.pageAlias) changed.add(TRACKING_FIELD.PAGE_ALIAS);
	if (current.pageId !== initial.pageId) changed.add(TRACKING_FIELD.PAGE_ID);
	if (current.showInHtmlSitemap !== initial.showInHtmlSitemap) changed.add(TRACKING_FIELD.HTML);
	if (current.showInXmlSitemap !== initial.showInXmlSitemap) changed.add(TRACKING_FIELD.XML);

	const xmlTurnedOn  =  current.showInXmlSitemap && !initial.showInXmlSitemap;
	const xmlTurnedOff = !current.showInXmlSitemap &&  initial.showInXmlSitemap;
	const xmlWasActive =  current.showInXmlSitemap ||  initial.showInXmlSitemap;

	const addDateChanged            = current.addToSitemapOn !== initial.addToSitemapOn;
	const addDateEffectivelyAdded   = xmlTurnedOn  && current.addToSitemapOn !== null;
	const addDateEffectivelyRemoved = xmlTurnedOff && initial.addToSitemapOn !== null;

	if (xmlWasActive && (addDateChanged || addDateEffectivelyAdded || addDateEffectivelyRemoved))
		changed.add(TRACKING_FIELD.XML_ADD_DATE);

	const removeDateChanged = current.showInXmlSitemap && current.removeFromSitemapOn !== initial.removeFromSitemapOn;
	const removeDateEffectivelyRemoved = xmlTurnedOff && initial.removeFromSitemapOn !== null;

	if (removeDateChanged || removeDateEffectivelyRemoved)
		changed.add(TRACKING_FIELD.XML_REMOVE_DATE);
	if (current.noIndex !== initial.noIndex) changed.add(TRACKING_FIELD.NO_INDEX);
	if (current.noFollow !== initial.noFollow) changed.add(TRACKING_FIELD.NO_FOLLOW);

	return SITEMAP_FIELD_ORDER.filter(field => changed.has(field));
};
